/**
 * AI Means Business API Client
 * 
 * Provides functions for interacting with:
 * - Services Catalog (public)
 * - Engagements (SER/BCO management)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Helper to get auth headers
 */
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

/**
 * Helper to handle API errors
 */
async function handleResponse(response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData?.detail?.message || errorData?.detail || 'API request failed');
    error.code = errorData?.detail?.error?.code || 'UNKNOWN_ERROR';
    error.details = errorData?.detail?.error?.details || {};
    error.status = response.status;
    throw error;
  }
  return response.json();
}

/**
 * Fetch the canonical services catalog (public endpoint)
 * 
 * @param {boolean} includePricing - Whether to request pricing (requires auth + eligibility)
 * @returns {Promise<{ok: boolean, services: Array, catalog_version: number, pricing_visible: boolean}>}
 */
export async function fetchServicesCatalog(includePricing = false) {
  const url = `${API_URL}/api/services/catalog${includePricing ? '?include_pricing=true' : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(includePricing ? getAuthHeaders() : {})
    }
  });
  return handleResponse(response);
}

/**
 * Check if the current user is eligible to see pricing
 * 
 * @returns {Promise<{ok: boolean, pricing_eligible: boolean, reason: string, message: string}>}
 */
export async function checkPricingEligibility() {
  const response = await fetch(`${API_URL}/api/user/pricing-eligibility`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  return handleResponse(response);
}

/**
 * Create a new engagement (requires auth)
 * 
 * @param {string} serviceId - SERVICE_A, SERVICE_B, or SERVICE_C
 * @returns {Promise<{ok: boolean, ser_id: string, bco_id: string, service_id: string, service_name: string, fixed_price_cents: number, lifecycle_state: string}>}
 */
export async function createEngagement(serviceId) {
  const response = await fetch(`${API_URL}/api/engagements/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ service_id: serviceId })
  });
  return handleResponse(response);
}

/**
 * Fetch current (active) engagement for logged-in user (requires auth)
 * 
 * @returns {Promise<{ok: boolean, active: boolean, ser?: object, service?: object}>}
 */
export async function fetchCurrentEngagement() {
  const response = await fetch(`${API_URL}/api/engagements/current`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  return handleResponse(response);
}

/**
 * Fetch the most recent engagement for logged-in user, including CLOSED engagements.
 * Used to allow users to access final reports after engagement closure.
 * 
 * @returns {Promise<{ok: boolean, has_engagement: boolean, is_active?: boolean, ser?: object, service?: object}>}
 */
export async function fetchLatestEngagement() {
  const response = await fetch(`${API_URL}/api/engagements/latest`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  return handleResponse(response);
}

/**
 * Transition engagement to a new state (requires auth)
 * 
 * @param {string} serId - Service Execution Record ID
 * @param {string} toState - Target lifecycle state
 * @param {string} changedBy - Who is making the change (default: "SYSTEM")
 * @returns {Promise<{ok: boolean, ser: object}>}
 */
export async function transitionEngagementState(serId, toState, changedBy = "SYSTEM") {
  const response = await fetch(`${API_URL}/api/engagements/${serId}/transition`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ to_state: toState, changed_by: changedBy })
  });
  return handleResponse(response);
}

/**
 * Fetch BCO for an engagement (requires auth)
 * 
 * @param {string} serId - Service Execution Record ID
 * @returns {Promise<{ok: boolean, bco: object}>}
 */
export async function fetchEngagementBCO(serId) {
  const response = await fetch(`${API_URL}/api/engagements/${serId}/bco`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  return handleResponse(response);
}

/**
 * Update BCO onboarding inputs (requires auth)
 * 
 * @param {string} serId - Service Execution Record ID
 * @param {object} onboardingInputs - New onboarding inputs
 * @returns {Promise<{ok: boolean, bco: object}>}
 */
export async function updateEngagementOnboarding(serId, onboardingInputs) {
  const response = await fetch(`${API_URL}/api/engagements/${serId}/bco/onboarding`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(onboardingInputs)
  });
  return handleResponse(response);
}

/**
 * Format price from cents to display string
 * 
 * @param {number} priceCents - Price in cents
 * @returns {string} Formatted price (e.g., "$2,000")
 */
export function formatPriceCents(priceCents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(priceCents / 100);
}

/**
 * Track ebook promotional offer engagement events
 * 
 * Valid event types:
 * - ebook_offer_shown: CTA became visible in User Portal
 * - ebook_modal_opened: User clicked CTA and saw promo modal
 * - ebook_outbound_click: User clicked "Okay" and was redirected
 * 
 * @param {string} eventType - Type of event to track
 * @param {string} serId - Optional SER ID for context
 * @returns {Promise<{ok: boolean}>}
 */
export async function trackEbookEvent(eventType, serId = null) {
  const response = await fetch(`${API_URL}/api/engagement-events/ebook`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ event_type: eventType, ser_id: serId })
  });
  return handleResponse(response);
}

/**
 * Get ebook offer metrics (admin only)
 * 
 * @returns {Promise<{ok: boolean, metrics: object, summary: object}>}
 */
export async function getEbookMetrics() {
  const response = await fetch(`${API_URL}/api/admin/ebook-metrics`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  });
  return handleResponse(response);
}

